import { Box } from "@chakra-ui/layout";
import { useEffect, useRef, useState } from "react";
const THRESHOLD = 0.0;

let sharedObserver: IntersectionObserver | null = null;
const observedElements = new Map<Element, (isIntersecting: boolean) => void>();
const getSharedObserver = () => {
  if (!sharedObserver) {
    sharedObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const callback = observedElements.get(entry.target);
          if (callback) {
            callback(entry.isIntersecting);
          }
        });
      },
      { threshold: THRESHOLD }
    );
  }
  return sharedObserver;
};

type LazyRenderProps = {
  children: () => React.ReactElement;
  placeholder: () => React.ReactElement;
};
export const LazyRender: React.FC<LazyRenderProps> = ({
  children,
  placeholder,
}) => {
  const [shouldRender, setShouldRender] = useState(false);
  const elementRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const element = elementRef.current;
    if (element) {
      const observer = getSharedObserver();
      const handleIntersection = (isIntersecting: boolean) => {
        setShouldRender(isIntersecting);
      };
      observedElements.set(element, handleIntersection);
      observer.observe(element);
      return () => {
        observedElements.delete(element);
        observer.unobserve(element);
      };
    }
  }, []);
  return (
    <Box ref={elementRef}>{shouldRender ? children() : placeholder()}</Box>
  );
};
type LazyListProps<T> = {
  elements: T[];
  children: (chunk: T[]) => React.ReactElement;
  placeholder: () => React.ReactElement;
};
export function LazyList<T>({
  elements,
  children,
  placeholder,
}: LazyListProps<T>) {
  return (
    <>
      {elements.map((element, index) => (
        <LazyRender key={index} placeholder={placeholder}>
          {() => children([element])}
        </LazyRender>
      ))}
    </>
  );
}
